import { Observable, Subject } from 'rxjs';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { ApiUrlInjectionToken } from '@mitsubishi/shared/data-access';

import { UnitTemplate, UnitTemplateFilters } from '../models';
import { UnitTemplateGroup, UnitTemplateGroups } from '../models';

const unitTemplatesCacheBuster$ = new Subject<void>();

@Injectable({
  providedIn: 'root',
})
export class UnitTemplateService {
  private readonly apiServerUrl: string;
  private readonly baseUrl: string;

  constructor(
    private http: HttpClient,
    @Inject(ApiUrlInjectionToken) apiServerUrl: string
  ) {
    this.apiServerUrl = apiServerUrl;
    this.baseUrl = apiServerUrl + '/unit-template';
  }

  bustUnitTemplatesCache() {
    unitTemplatesCacheBuster$.next();
  }

  getUnitTemplates(
    projectId: string,
    filters: UnitTemplateFilters,
    selectedGroup?: UnitTemplateGroup
  ): Observable<UnitTemplate[]> {
    const paramsObj = {};
    if (selectedGroup && !selectedGroup.period) {
      paramsObj['groupId'] = selectedGroup.id;
    }
    if (selectedGroup && selectedGroup.period) {
      paramsObj['recentType'] = selectedGroup.id;
    }
    if (filters.classId) {
      paramsObj['classId'] = filters.classId;
    }
    if (filters.brandId) {
      paramsObj['brandId'] = filters.brandId;
    }
    if (filters.segmentId) {
      paramsObj['segmentId'] = filters.segmentId;
    }
    if (filters.search) {
      paramsObj['search'] = filters.search;
    }
    const params = new HttpParams({ fromObject: paramsObj });

    return this.http.get<UnitTemplate[]>(`${this.apiServerUrl}/project/${projectId}/unit-templates`, { params });
  }

  getUnitTemplateGroups(): Observable<UnitTemplateGroups> {
    return this.http.get<UnitTemplateGroups>(`${this.baseUrl}/groups`);
  }

  addUnitGroup(name: string, isOrganization: boolean) {
    return this.http.post(`${this.baseUrl}/groups`, { name: name, isOrganizationGroup: isOrganization });
  }

  updateUnitGroupName(groupId: string, description: string): Observable<{ name: string }> {
    return this.http.patch<{ name: string }>(`${this.baseUrl}/groups/${groupId}`, { name: description });
  }

  deleteUnitGroup(groupId: string) {
    return this.http.delete(`${this.baseUrl}/groups/${groupId}`);
  }

  deleteUnitTemplate(unitTemplateId: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${unitTemplateId}`);
  }

  createUnitTemplate(
    projectId: string,
    unitId: string,
    unitTemplateName: string,
    groupId: string,
    alternativeId: string
  ): Observable<null> {
    return this.http.post<null>(this.baseUrl, { projectId, unitId, unitTemplateName, groupId, alternativeId });
  }

  updateUnitDescription(unitTemplateId: string, description: string): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/${unitTemplateId}/description`, { description: description });
  }

  moveUnit(unitTemplateId: string, groupId: string): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/${unitTemplateId}/move`, { groupId: groupId });
  }
}
